import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import usePhotoFinderSubscriptionsCheckout from '../../usePhotoFinderSubscriptionCheckout';
import getPhotoFinderPricingTiers from '../../../../AlbumFolder/AlbumFolderSettings/AlbumFolderDeliveryPreference/queries/getPhotoFinderPricingTiers';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD } from '../PhotoFinderSubscriptionDetails/helpers';
import {
  formatNumber,
  formatPriceForDisplay,
} from '../../../../../../../util/format-money';
import PhotoFinderSubscriptionTable from '../PhotoFinderSubscriptionTable';
import Button from '../../../../../../../components/global/Button';
import { PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL } from '../../../../Album/AlbumCreateWizard/AlbumCreateDeliveryPreferenceStep/PhotoManagerCreditCardCaptureModal';

const STEPS = {
  PHOTO_FINDER_SUB: 'PHOTO_FINDER_SUB',
  PHOTO_FINDER_CREDITS: 'PHOTO_FINDER_CREDITS',
};

const propTypes = {
  onCheckoutSuccess: PropTypes.func,
  onPayPerPhoto: PropTypes.func,
};

const PhotoFinderSubscriptionCheckout = ({
  onCheckoutSuccess,
  onPayPerPhoto,
}) => {
  const {
    photoFinderSubscriptionItems,
    account,
    onCheckout,
    checkingOut,
  } = usePhotoFinderSubscriptionsCheckout({ onCheckoutSuccess });

  const { data: { photoFinderPricingTiers = [] } = {} } = useQuery(
    getPhotoFinderPricingTiers,
  );

  const openModal = useOpenModal();
  const [selectedSubscriptionItem, setSelectedSubscriptionItem] = useState(
    photoFinderSubscriptionItems?.[0],
  );
  const [currentStep, setCurrentStep] = useState(STEPS.PHOTO_FINDER_SUB);
  const activeSubscription = account?.photo_finder_subscriptions?.records?.[0];
  const selectedSubscriptionIsMonthly =
    selectedSubscriptionItem?.billing_period ===
    PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY;

  return (
    <Fragment>
      {currentStep === STEPS.PHOTO_FINDER_SUB && (
        <Fragment>
          <div className="d-flex justify-content-center flex-column">
            {photoFinderSubscriptionItems?.length > 0 && (
              <PhotoFinderSubscriptionTable
                selectable
                photoFinderSubscriptionItems={photoFinderSubscriptionItems}
                onSelectionChange={subscriptionItem =>
                  setSelectedSubscriptionItem(subscriptionItem)
                }
                buttonWrapperClassName="d-flex justify-content-center"
                currentSubscriptionPrice={
                  activeSubscription?.subscription_item?.price?.value
                }
              />
            )}
            <div className="row px-2 mb-4">
              <div className="col-12 col-md-6 px-0 pr-md-1">
                <Button
                  btnBlock
                  btnSize="lg"
                  onClick={() => {
                    if (account?.default_payment_method) {
                      onCheckout({
                        photoFinderSubscriptionItemId:
                          selectedSubscriptionItem?.id,
                        stripeToken: undefined,
                      });
                    } else {
                      openModal(PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL, {
                        title: <h3>Photo Finder Checkout</h3>,
                        description: (
                          <p>
                            {`${
                              selectedSubscriptionIsMonthly
                                ? 'Monthly Plan'
                                : 'Yearly Plan'
                            } - Your subscription will start immediately.`}
                          </p>
                        ),
                        buttonLabel: `Confirm & Pay - $${formatPriceForDisplay(
                          selectedSubscriptionItem?.price?.value,
                          true,
                        )}`,
                        onSuccess: stripeToken => {
                          onCheckout({
                            photoFinderSubscriptionItemId:
                              selectedSubscriptionItem?.id,
                            stripeToken,
                          });
                        },
                      });
                    }
                  }}
                  disabled={checkingOut}
                >
                  {checkingOut ? 'Checking out...' : 'Upgrade Now'}
                </Button>
              </div>
              <div className="col-12 col-md-6 px-0 pl-md-1 mt-2 mt-md-0">
                <Button
                  btnOutline
                  btnBlock
                  btnSize="lg"
                  onClick={onPayPerPhoto}
                >
                  Pay per Photo
                </Button>
                <Button
                  btnStyle="link"
                  btnBlock
                  className="mb-0 mt-1 text-xs text-primary text-center"
                  onClick={() => setCurrentStep(STEPS.PHOTO_FINDER_CREDITS)}
                >
                  See Pricing
                </Button>
              </div>
            </div>
          </div>
        </Fragment>
      )}
      {currentStep === STEPS.PHOTO_FINDER_CREDITS && (
        <Fragment>
          <h3 className="mb-2 text-center">Photo Finder Credits Pricing</h3>
          <div className="table-responsive horizontal-scroller">
            <table className="table table-striped mb-3">
              <thead>
                <tr className="text-dark">
                  <th className="text-black text-left">Photos per year</th>
                  <th className="text-black">Price per photo</th>
                </tr>
              </thead>

              <tbody>
                {photoFinderPricingTiers.map(priceTier => {
                  const isLast = priceTier.to === null;

                  return (
                    <tr key={priceTier.id}>
                      <td className="text-left">
                        {isLast
                          ? `${formatNumber(priceTier.from)}+`
                          : formatNumber(priceTier.to)}
                      </td>
                      <td>
                        ${(priceTier.price / 100).toString().padEnd(5, '0')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button
            className="my-2 text-center"
            btnStyle="link"
            btnBlock
            onClick={() => setCurrentStep(STEPS.PHOTO_FINDER_SUB)}
          >
            Back to Subscription Purchase
          </Button>
        </Fragment>
      )}
    </Fragment>
  );
};

PhotoFinderSubscriptionCheckout.propTypes = propTypes;

export default PhotoFinderSubscriptionCheckout;
